/**
 * TextFormattingIntegration - Integration component for MS Word-style text formatting
 * 
 * Integrates all formatting components with ClientFirstDocumentEditor:
 * - SelectionManagerEnhanced for text selection
 * - CharacterFormatManager for format management  
 * - TextContextMenu for right-click formatting
 * - Format persistence with document saving/loading
 */

import React, { useState, useEffect, useRef } from 'react';
import { TextContextMenu, TextFormat, ContextMenuProps } from '../components/TextContextMenu';
import { CharacterFormatManager, CharacterFormat } from '../formatting/CharacterFormatManager';
import { LocalDocumentEngine } from '../core/LocalDocumentEngine';

export interface TextFormattingIntegrationProps {
  documentEngine: LocalDocumentEngine;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  currentBackendLayoutPlan: any; // Backend layout plan for hit testing
  paperDimensions: any; // Paper dimensions for content rect
  onPageSetup: () => void;
  onFormatChange?: (hasFormatting: boolean) => void;
  // Integration with existing PaperCanvas selection system
  onSelectionChange?: (start: number, end: number) => void;
  selectionStart?: number;
  selectionEnd?: number;
}

export const TextFormattingIntegration: React.FC<TextFormattingIntegrationProps> = ({
  documentEngine,
  canvasRef,
  currentBackendLayoutPlan,
  paperDimensions,
  onPageSetup,
  onFormatChange,
  onSelectionChange,
  selectionStart,
  selectionEnd
}) => {
  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    isOpen: boolean;
    position: { x: number; y: number };
    selectedText: string;
    currentFormat: CharacterFormat;
    selection: any;
  }>({
    isOpen: false,
    position: { x: 0, y: 0 },
    selectedText: '',
    currentFormat: {},
    selection: null
  });

  // Formatting managers
  const formatManagerRef = useRef<CharacterFormatManager | null>(null);

  // Initialize formatting system - simplified to work with existing PaperCanvas selection
  useEffect(() => {
    console.log('[TextFormattingIntegration] Component mounted', {
      hasDocumentEngine: !!documentEngine,
      hasCanvas: !!canvasRef.current,
      canvasRefType: typeof canvasRef.current
    });
    
    if (!documentEngine || !canvasRef.current) {
      console.warn('[TextFormattingIntegration] Missing dependencies:', {
        documentEngine: !!documentEngine,
        canvas: !!canvasRef.current
      });
      return;
    }

    console.log('[TextFormattingIntegration] Initializing formatting system that integrates with PaperCanvas selection');

    // Create format manager only - no need for SelectionManager since PaperCanvas handles selection
    const formatManager = new CharacterFormatManager(documentEngine);
    formatManagerRef.current = formatManager;

    // Listen for format changes
    formatManager.on('formatChanged', ({ allFormats }: any) => {
      const hasFormatting = allFormats.length > 0;
      onFormatChange?.(hasFormatting);
      
      console.log(`[TextFormattingIntegration] Format changed. Active ranges: ${allFormats.length}`);
    });

    // Context menu handler for right-click
    const handleRightClick = (event: MouseEvent) => {
      console.log('🖱️ [TEXT_FORMATTING] Right-click detected!', {
        x: event.clientX,
        y: event.clientY,
        hasSelection: selectionStart !== undefined && selectionEnd !== undefined && selectionStart !== -1
      });
      
      // Get selected text from the existing PaperCanvas selection
      const selectedText = (selectionStart !== undefined && selectionEnd !== undefined && selectionStart !== -1) 
        ? documentEngine.getText().slice(selectionStart, selectionEnd)
        : '';
      
      setContextMenu({
        isOpen: true,
        position: { x: event.clientX, y: event.clientY },
        selectedText: selectedText || 'No selection',
        currentFormat: {},
        selection: (selectionStart !== undefined && selectionEnd !== undefined) 
          ? { start: selectionStart, end: selectionEnd, direction: 'forward' }
          : { start: 0, end: 0, direction: 'forward' }
      });
      
      // Prevent browser context menu
      event.preventDefault();
      event.stopPropagation();
      
      console.log('✅ [TEXT_FORMATTING] Context menu shown with selection:', selectedText);
    };

    const canvas = canvasRef.current;
    // Only add context menu listener - let PaperCanvas handle selection
    canvas.addEventListener('contextmenu', handleRightClick);
    console.log('✅ [TEXT_FORMATTING] Context menu listener added');

    // Test basic functionality
    setTimeout(() => {
      console.log('🧪 [TEXT_FORMATTING] Running basic functionality test...');
      console.log('🧪 Document text length:', documentEngine.getText().length);
      console.log('🧪 Format manager exists:', !!formatManager);
      console.log('🧪 Current selection:', { selectionStart, selectionEnd });
    }, 1000);

    // Cleanup
    return () => {
      canvas.removeEventListener('contextmenu', handleRightClick);
      console.log('🧹 [TEXT_FORMATTING] Event listeners cleaned up');
    };
  }, [documentEngine, canvasRef, onFormatChange, selectionStart, selectionEnd]);

  // Handle format application from context menu
  const handleFormatApply = (format: Partial<TextFormat>) => {
    if (!formatManagerRef.current) return;

    console.log('[TextFormattingIntegration] Applying format:', format);

    // Convert TextFormat to CharacterFormat
    const characterFormat: Partial<CharacterFormat> = {
      fontFamily: format.fontFamily,
      fontSize: format.fontSize,
      fontColor: format.fontColor,
      backgroundColor: format.backgroundColor,
      bold: format.bold,
      italic: format.italic,
      underline: format.underline,
      alignment: format.alignment
    };

    // Apply format using existing PaperCanvas selection
    if (selectionStart !== undefined && selectionEnd !== undefined && selectionStart !== -1) {
      const selection = { start: selectionStart, end: selectionEnd, direction: 'forward' as const };
      formatManagerRef.current.applyFormat(selection, characterFormat);
      console.log('[TextFormattingIntegration] Format applied successfully to PaperCanvas selection');
      
      // Update context menu with new format
      const updatedFormat = formatManagerRef.current.getFormatAtPosition(selection.start);
      setContextMenu(prev => ({
        ...prev,
        currentFormat: updatedFormat
      }));
    } else {
      console.warn('[TextFormattingIntegration] Failed to apply format - no selection from PaperCanvas');
    }
  };

  // Handle context menu close
  const handleContextMenuClose = () => {
    setContextMenu(prev => ({ ...prev, isOpen: false }));
  };

  // Public API for external access
  useEffect(() => {
    // Expose formatting API to parent component
    if (canvasRef.current && formatManagerRef.current) {
      (canvasRef.current as any).formattingAPI = {
        // Format management - uses existing PaperCanvas selection
        applyFormat: (format: Partial<CharacterFormat>) => {
          if (selectionStart !== undefined && selectionEnd !== undefined && selectionStart !== -1) {
            const selection = { start: selectionStart, end: selectionEnd, direction: 'forward' as const };
            return formatManagerRef.current?.applyFormat(selection, format);
          }
        },
        clearFormat: () => {
          if (selectionStart !== undefined && selectionEnd !== undefined && selectionStart !== -1) {
            formatManagerRef.current?.clearFormatInRange(selectionStart, selectionEnd);
          }
        },
        getCurrentFormat: () => 
          formatManagerRef.current?.getFormatAtPosition(selectionStart || 0),
        
        // Selection management - delegates to PaperCanvas
        getSelection: () => (selectionStart !== undefined && selectionEnd !== undefined) 
          ? { start: selectionStart, end: selectionEnd, direction: 'forward' as const }
          : null,
        
        // Persistence
        getFormattingData: () => 
          formatManagerRef.current?.serializeFormats(),
        loadFormattingData: (data: any) => 
          formatManagerRef.current?.loadFormats(data),
        
        // Utilities
        hasFormatting: () => 
          (formatManagerRef.current?.getAllFormatRanges()?.length || 0) > 0,
        clearAllFormatting: () => 
          formatManagerRef.current?.clearAllFormats()
      };
    }
  }, [canvasRef, selectionStart, selectionEnd]);

  return (
    <>
      {/* Debug indicator - remove after testing */}
      <div 
        className="fixed top-2 right-2 bg-green-500 text-white px-2 py-1 text-xs rounded z-50"
        title="Text Formatting System Active"
      >
        📝 Formatting Ready
      </div>
      
      <TextContextMenu
        isOpen={contextMenu.isOpen}
        position={contextMenu.position}
        selectedText={contextMenu.selectedText}
        currentFormat={{
          fontFamily: contextMenu.currentFormat.fontFamily,
          fontSize: contextMenu.currentFormat.fontSize,
          fontColor: contextMenu.currentFormat.fontColor,
          backgroundColor: contextMenu.currentFormat.backgroundColor,
          bold: contextMenu.currentFormat.bold,
          italic: contextMenu.currentFormat.italic,
          underline: contextMenu.currentFormat.underline,
          alignment: contextMenu.currentFormat.alignment
        }}
        onClose={handleContextMenuClose}
        onFormatApply={handleFormatApply}
        onPageSetup={onPageSetup}
      />
    </>
  );
};

// Hook for easy integration
export const useTextFormatting = (
  documentEngine: LocalDocumentEngine | null,
  canvasRef: React.RefObject<HTMLCanvasElement | null>
) => {
  const [hasFormatting, setHasFormatting] = useState(false);
  
  const formatAPI = canvasRef.current ? (canvasRef.current as any).formattingAPI : null;
  
  return {
    hasFormatting,
    formatAPI,
    // Convenience methods
    applyBold: () => formatAPI?.applyFormat({ bold: true }),
    applyItalic: () => formatAPI?.applyFormat({ italic: true }),
    applyUnderline: () => formatAPI?.applyFormat({ underline: true }),
    setFontSize: (size: number) => formatAPI?.applyFormat({ fontSize: size }),
    setFontColor: (color: string) => formatAPI?.applyFormat({ fontColor: color }),
    highlight: (color: string) => formatAPI?.applyFormat({ backgroundColor: color }),
    clearFormatting: () => formatAPI?.clearFormat()
  };
};
